import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, Send, Loader2, ChevronLeft, Eye, Check, Plus,
  Sparkles, Inbox, Paperclip, Upload, FileText, X, Search,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { RecipientTags } from "../components/RecipientTags";
import { RichTextEditor } from "../components/RichTextEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactLabel = (c) => {
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  const role = c.role ? ` · ${c.role}` : "";
  return `${name || c.email}${role} — ${c.email}`;
};

const ATTACH_CAP = 40 * 1024 * 1024;
const money = (v) => (v || v === 0
  ? new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(v)
  : "—");
const fmtBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};

// Every merge token, in plain English. Prospects also expose {{source}}.
const BASE_TOKENS = [
  { key: "first_name", label: "First name", eg: "Nathan" },
  { key: "contact_name", label: "Full contact name", eg: "Nathan Wood" },
  { key: "company_name", label: "Company name", eg: "B&B Street Sweeping" },
  { key: "contact_email", label: "Contact email" },
  { key: "contact_phone", label: "Contact phone" },
  { key: "rep_name", label: "Your name (sender)" },
];

/* ── Lightweight rich-text editor — writes clean HTML, no HTML knowledge needed ── */
export default function EmailComposePage({ entity = "prospect", recordId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.emailPrefill || null; // e.g. from "Email quote"
  const isClient = entity === "client";
  const noun = isClient ? "client" : "prospect";
  const base = isClient
    ? `/customer-success/clients/${encodeURIComponent(recordId)}`
    : `/sales/prospects/${encodeURIComponent(recordId)}`;
  const backTo = isClient ? `/clients/${encodeURIComponent(recordId)}` : `/prospects/${encodeURIComponent(recordId)}`;

  const [record, setRecord] = useState(null);
  const [me, setMe] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [compose, setCompose] = useState({
    template_id: "", subject: prefill?.subject || "", body_html: prefill?.body_html || "", from_name: "", from_email: "",
  });
  const [toList, setToList] = useState(prefill?.to_email ? [prefill.to_email] : []);
  const [sendMode, setSendMode] = useState("together");
  const [cc, setCc] = useState(prefill?.cc || []);
  const [bcc, setBcc] = useState(prefill?.bcc || []);
  const [showCc, setShowCc] = useState((prefill?.cc || []).length > 0);
  const [showBcc, setShowBcc] = useState((prefill?.bcc || []).length > 0);
  const [resetKey, setResetKey] = useState(0);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [aiInstruction, setAiInstruction] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [attachPdf, setAttachPdf] = useState(!!prefill?.quote_id); // default on for quote emails
  const quoteId = prefill?.quote_id || null;
  const quoteNumber = prefill?.quote_number || null;
  const [latestQuote, setLatestQuote] = useState(null); // most recent quote for this company

  // Attachments (library documents + one-off uploads)
  const [attachments, setAttachments] = useState([]); // {kind:'doc'|'upload', id?, path?, filename, size}
  const [pickerOpen, setPickerOpen] = useState(false);
  const [docList, setDocList] = useState([]);
  const [docSearch, setDocSearch] = useState("");
  const [docLoading, setDocLoading] = useState(false);
  const [docSelected, setDocSelected] = useState({});
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const editorRef = useRef(null);
  const subjectRef = useRef(null);
  const lastFocused = useRef("body");
  const fromEdited = useRef(false); // once the rep edits From, stop auto-defaulting

  const tokens = isClient ? BASE_TOKENS : [...BASE_TOKENS, { key: "source", label: "Lead source" }];

  // Load record + templates + settings + me + contacts
  useEffect(() => {
    axios.get(`${API}/auth/me`).then((r) => setMe(r.data)).catch(() => {});
    axios.get(`${API}/sales/email-templates`).then((r) => setTemplates(r.data.templates || [])).catch(() => {});
    axios.get(`${API}/sales/settings`).then((r) => setSettings(r.data.settings)).catch(() => {});
    axios.get(`${API}${base}`).then((r) => setRecord(isClient ? r.data.client : r.data.prospect)).catch(() => {});
    axios.get(`${API}${base}/contacts`)
      .then((r) => {
        const cts = (r.data.contacts || []).filter((c) => (c.email || "").trim());
        setContacts(cts);
        setToList((t) => (t.length ? t : (cts[0]?.email ? [cts[0].email] : [])));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  // Default To from record if contacts didn't provide one
  useEffect(() => {
    if (!record) return;
    const fallback = record.contact_email || record.email || "";
    setToList((t) => (t.length || !fallback ? t : [fallback]));
  }, [record]);

  // Default From to the LOGGED-IN person; the sales settings sender is only a
  // last-resort fallback (never let it lock in before /auth/me resolves).
  useEffect(() => {
    if (fromEdited.current) return;
    const name = me?.name || settings?.default_from_name || "";
    const email = me?.email || settings?.default_from_email || "";
    setCompose((c) => ({ ...c, from_name: name, from_email: email }));
  }, [me, settings]);

  const onSelectTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    setCompose((c) => ({ ...c, template_id: tid, subject: t?.subject ?? c.subject, body_html: t?.body_html ?? c.body_html }));
    setResetKey((k) => k + 1); // push new body into the editor
  };

  // Debounced live preview. The primary recipient decides whose name the
  // tokens use, so the preview shows what that person will actually read.
  const primaryTo = toList[0] || "";
  const refreshPreview = useCallback(async () => {
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}${base}/email/preview`, {
        subject: compose.subject, body_html: compose.body_html,
        from_name: compose.from_name, to_email: primaryTo,
      });
      setPreviewHtml(res.data.body_html || "");
    } catch (e) { /* keep last preview */ }
    finally { setPreviewing(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, compose.subject, compose.body_html, compose.from_name, primaryTo]);

  useEffect(() => {
    const id = setTimeout(refreshPreview, 500);
    return () => clearTimeout(id);
  }, [refreshPreview]);

  const insertToken = (key) => {
    const tok = `{{${key}}}`;
    if (lastFocused.current === "subject" && subjectRef.current) {
      const el = subjectRef.current;
      const s = el.selectionStart ?? el.value.length;
      const e = el.selectionEnd ?? el.value.length;
      const nv = el.value.slice(0, s) + tok + el.value.slice(e);
      setCompose((c) => ({ ...c, subject: nv }));
      requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + tok.length, s + tok.length); });
    } else {
      editorRef.current?.insertToken(tok);
    }
  };

  const draftWithAI = async () => {
    setDrafting(true);
    try {
      const res = await axios.post(`${API}${base}/email/draft`, { instruction: aiInstruction || null });
      setCompose((c) => ({ ...c, subject: res.data.subject || c.subject, body_html: res.data.body_html || "" }));
      setResetKey((k) => k + 1);
      toast.success("Draft ready — review and edit before sending");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" && d) ? d : "AI drafting failed");
    } finally { setDrafting(false); }
  };

  const sendTestCopy = async () => {
    if (!compose.subject.trim() || !compose.body_html.trim()) { toast.error("Write a subject and body first"); return; }
    setSendingTest(true);
    try {
      const res = await axios.post(`${API}${base}/email/test`, {
        template_id: compose.template_id || null,
        subject: compose.subject, body_html: compose.body_html,
        from_name: compose.from_name || null, from_email: compose.from_email || null,
      });
      toast.success(`Test copy sent to ${res.data.to}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" && d && !d.trim().startsWith("<")) ? d : "Failed to send test copy");
    } finally { setSendingTest(false); }
  };

  const removeAttachment = (idx) => setAttachments((a) => a.filter((_, i) => i !== idx));
  const totalSize = attachments.reduce((s, a) => s + (a.size || 0), 0);
  const overCap = totalSize > ATTACH_CAP;

  // The most recent live quote for this company — one click to attach its PDF.
  useEffect(() => {
    if (quoteId) return; // already emailing a specific quote
    axios.get(`${API}/quotes`, { params: { link_type: entity, link_id: recordId } })
      .then((r) => {
        const q = (r.data.quotes || []).find((x) => !["void", "voided"].includes(x.status));
        setLatestQuote(q || null);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, recordId, quoteId]);

  const quoteFilename = (attachPdf && (quoteNumber || latestQuote?.quote_number))
    ? `${quoteNumber || latestQuote?.quote_number}.pdf` : null;

  const fetchDocs = useCallback(async () => {
    setDocLoading(true);
    try {
      const r = await axios.get(`${API}/documents`, { params: docSearch ? { q: docSearch } : {} });
      setDocList(r.data.documents || []);
    } catch { setDocList([]); } finally { setDocLoading(false); }
  }, [docSearch]);

  const openPicker = () => { setDocSelected({}); setPickerOpen(true); fetchDocs(); };

  const addSelectedDocs = async () => {
    const ids = Object.keys(docSelected).filter((k) => docSelected[k]);
    const existing = new Set(attachments.filter((a) => a.kind === "doc").map((a) => a.id));
    const toAdd = ids.filter((id) => !existing.has(id));
    const results = await Promise.all(toAdd.map(async (id) => {
      try {
        const r = await axios.get(`${API}/documents/${id}`);
        const d = r.data.document; const vs = r.data.versions || [];
        const cur = vs.find((v) => v.id === d.current_version_id) || vs[0] || {};
        return { kind: "doc", id, filename: cur.filename || d.current_filename || d.title, size: cur.size || 0 };
      } catch { return null; }
    }));
    setAttachments((a) => [...a, ...results.filter(Boolean)]);
    setPickerOpen(false);
  };

  const onUploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const mb = (file.size / 1048576).toFixed(1);
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`${file.name} is ${mb}MB — attachments have to be under 25MB`);
          continue;
        }
        const fd = new FormData(); fd.append("file", file);
        try {
          const r = await axios.post(`${API}/sales/email/upload-attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          setAttachments((a) => [...a, { kind: "upload", path: r.data.path, filename: r.data.filename, size: r.data.size }]);
        } catch (e) {
          // A 413 is the web server refusing the size before the API ever sees
          // it — say that, because "couldn't upload" sends people hunting the app.
          const status = e?.response?.status;
          const detail = e?.response?.data?.detail;
          if (status === 413) {
            toast.error(`${file.name} (${mb}MB) was rejected by the server as too large. The upload limit needs raising on the server — the app itself allows up to 25MB.`);
          } else {
            toast.error(detail || `Couldn't upload ${file.name} (${mb}MB)${status ? ` — server said ${status}` : " — no response from the server"}`);
          }
        }
      }
    } finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const sendEmail = async () => {
    if (!toList.length) { toast.error(`Choose at least one recipient`); return; }
    if (!compose.subject.trim() || !compose.body_html.trim()) { toast.error("Subject and body are required"); return; }
    if (overCap) { toast.error("Attachments exceed the 40MB limit — remove some files"); return; }
    const mode = toList.length > 1 ? sendMode : "together";
    setSending(true);
    try {
      await axios.post(`${API}${base}/email/send`, {
        template_id: compose.template_id || null,
        subject: compose.subject,
        body_html: compose.body_html,
        from_name: compose.from_name || null,
        from_email: compose.from_email || null,
        to_email: toList[0] || null,
        to_emails: toList,
        send_mode: mode,
        cc, bcc,
        quote_id: quoteId || (attachPdf && latestQuote ? latestQuote.id : null),
        attach_pdf: attachPdf && !!(quoteId || latestQuote),
        document_ids: attachments.filter((a) => a.kind === "doc").map((a) => a.id),
        upload_attachments: attachments.filter((a) => a.kind === "upload").map((a) => ({ path: a.path, filename: a.filename })),
      });
      toast.success(
        toList.length === 1 ? `Email sent to ${toList[0]}`
          : mode === "separate" ? `Sent ${toList.length} separate emails, one to each contact`
            : `Email sent to ${toList.length} contacts`,
        { description: (cc.length ? `Copied to ${cc.length}. ` : "")
            + (bcc.length ? `Blind-copied to ${bcc.length}.` : "") || undefined });
      navigate(backTo);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "string" && detail.trim() && !detail.trim().startsWith("<")) ? detail : "Failed to send email";
      toast.error(msg);
    } finally { setSending(false); }
  };

  const company = record?.company_name || "";
  const crumbRoot = isClient ? "Customer Success" : "Sales";
  const crumbList = isClient ? "Clients" : "Prospects";

  return (
    <div className="min-h-full bg-[#EDEAE3] px-5 py-4" data-testid="email-compose-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-[12px] text-[#8A857A] mb-2">
        <button onClick={() => navigate(backTo)} className="hover:text-[#1A1A1A]">{crumbRoot}</button>
        <span>/</span>
        <button onClick={() => navigate(backTo)} className="hover:text-[#1A1A1A]">{crumbList}</button>
        <span>/</span>
        <button onClick={() => navigate(backTo)} className="hover:text-[#1A1A1A]">{company || "—"}</button>
        <span>/</span>
        <span className="text-[#59554D] font-medium">Email</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(backTo)} className="w-8 h-8 rounded-md flex items-center justify-center text-[#59554D] hover:bg-white" data-testid="email-back-btn">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <Mail className="w-5 h-5 text-[#8A6D00]" />
          <div>
            <h1 className="text-[20px] font-bold text-[#1A1A1A]" style={{ fontFamily: "Manrope, sans-serif" }}>
              Send email{company ? ` — ${company}` : ""}
            </h1>
            <p className="text-[12px] text-[#8A857A]">Tokens are filled in automatically from this {noun}.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto sm:justify-end">
          <Button variant="ghost" onClick={() => navigate(backTo)} className="text-[#59554D]" data-testid="email-cancel-btn">Cancel</Button>
          <Button variant="outline" onClick={sendTestCopy} disabled={sendingTest}
            className="border-[#DAD5CA] bg-white text-[#59554D] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]" data-testid="email-test-btn">
            {sendingTest ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Inbox className="w-4 h-4 mr-1.5" />} Send me a test copy
          </Button>
          <Button onClick={sendEmail} disabled={sending} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] font-semibold" data-testid="email-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />} Send email
          </Button>
        </div>
      </div>

      {/* Quote email banner + attach-PDF toggle */}
      {quoteId && (
        <div className="mb-4 flex items-center justify-between gap-3 flex-wrap rounded-xl border border-[#E0B800] bg-[#FFF8D6] px-4 py-2.5" data-testid="email-quote-banner">
          <span className="text-[13px] text-[#1A1A1A]">
            Emailing quote <strong>{quoteNumber}</strong> — the accept link is already in the message.
          </span>
          <label className="flex items-center gap-2 text-[13px] text-[#1A1A1A] cursor-pointer select-none" data-testid="email-attach-pdf">
            <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)}
              className="w-4 h-4 accent-[#8A6D00] cursor-pointer" data-testid="email-attach-pdf-checkbox" />
            Attach the quote PDF
          </label>
        </div>
      )}

      {/* Split: form (left) + live preview (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(380px,480px)] gap-5 items-start">
        {/* LEFT — form */}
        <div className="bg-white border border-[#DAD5CA] rounded-xl p-5 space-y-4" style={{ boxShadow: "var(--shadow-card)" }}>
          <div>
            <label className="text-xs text-[#59554D]">Template</label>
            <Select value={compose.template_id || "none"} onValueChange={(v) => onSelectTemplate(v === "none" ? "" : v)}>
              <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] w-full" data-testid="email-template"><SelectValue placeholder="Choose a template" /></SelectTrigger>
              <SelectContent className="bg-white border-[#DAD5CA]">
                <SelectItem value="none">No template (write from scratch)</SelectItem>
                {templates.filter((t) => t.active).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#59554D]">From name</label>
              <Input value={compose.from_name} onChange={(e) => { fromEdited.current = true; setCompose({ ...compose, from_name: e.target.value }); }}
                className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="email-from-name" />
            </div>
            <div>
              <label className="text-xs text-[#59554D]">From email</label>
              <Input value={compose.from_email} onChange={(e) => { fromEdited.current = true; setCompose({ ...compose, from_email: e.target.value }); }}
                placeholder="you@equipr.software" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="email-from-email" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#59554D]">Recipients</label>
            {contacts.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="email-recipient-picker">
                {contacts.map((c) => {
                  const on = toList.some((e) => e.toLowerCase() === (c.email || "").toLowerCase());
                  return (
                    <button key={c.id || c.email} type="button"
                      onClick={() => setToList((t) => (on
                        ? t.filter((e) => e.toLowerCase() !== c.email.toLowerCase())
                        : [...t, c.email]))}
                      data-testid={`email-recipient-${c.email}`} aria-pressed={on}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] border transition-colors ${on
                        ? "bg-[#1A1A1A] border-[#1A1A1A] text-white"
                        : "bg-white border-[#DAD5CA] text-[#59554D] hover:bg-[#F6F4EF]"}`}>
                      {on ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                      {contactLabel(c)}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-[#8A857A] mt-1">No saved contacts — type an email below.</p>
            )}
          </div>
          <div>
            <RecipientTags label="To" value={toList} onChange={setToList} testid="email-to"
              suggestions={contacts.map((c) => c.email)} />
            {(!showCc || !showBcc) && (
              <div className="flex items-center gap-3 mt-1.5">
                {!showCc && (
                  <button type="button" onClick={() => setShowCc(true)} data-testid="email-add-cc"
                    className="text-[11.5px] font-semibold text-[#8A6D00] hover:underline">Add Cc</button>
                )}
                {!showBcc && (
                  <button type="button" onClick={() => setShowBcc(true)} data-testid="email-add-bcc"
                    className="text-[11.5px] font-semibold text-[#8A6D00] hover:underline">Add Bcc</button>
                )}
              </div>
            )}
          </div>
          {toList.length > 1 && (
            <div data-testid="email-send-mode">
              <div className="inline-flex p-0.5 rounded-[9px] bg-[#F4F2ED] border border-[#DAD5CA]">
                {[["together", "One email to all"], ["separate", "Separate email to each"]].map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setSendMode(v)}
                    data-testid={`email-send-mode-${v}`}
                    className={`px-3 py-1 rounded-[7px] text-[12px] font-semibold transition-colors ${sendMode === v
                      ? "bg-white text-[#1A1A1A] shadow-sm" : "text-[#59554D] hover:text-[#1A1A1A]"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-[11.5px] text-[#8A857A] mt-1.5" data-testid="email-send-mode-hint">
                {sendMode === "separate"
                  ? `Each person gets their own copy, addressed to them by name, and cannot see the others.`
                  : `All ${toList.length} are on the one email and can see each other. Names fill in from ${primaryTo}, so write "Hi all" rather than a single name.`}
              </p>
            </div>
          )}
          {showCc && (
            <RecipientTags label="Cc" value={cc} onChange={setCc} testid="email-cc"
              suggestions={contacts.map((c) => c.email).filter((e) => e && !toList.some((t) => t.toLowerCase() === e.toLowerCase()))} />
          )}
          {showBcc && (
            <RecipientTags label="Bcc" value={bcc} onChange={setBcc} testid="email-bcc"
              suggestions={[me?.email].filter(Boolean)} />
          )}
          <div>
            <label className="text-xs text-[#59554D]">Subject</label>
            <Input ref={subjectRef} value={compose.subject}
              onFocus={() => { lastFocused.current = "subject"; }}
              onChange={(e) => setCompose({ ...compose, subject: e.target.value })}
              className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="email-subject" />
          </div>
          <div onFocus={() => { lastFocused.current = "body"; }}>
            <label className="text-xs text-[#59554D] mb-1 block">Message</label>
            {/* Draft with AI — writes a first-pass email you can edit */}
            <div className="flex items-center gap-2 mb-2 p-2 rounded-[10px] border border-[#E6E2D9] bg-[#FBFAF7]" data-testid="email-ai-draft-row">
              <Sparkles className="w-4 h-4 text-[#8A6D00] shrink-0" />
              <Input
                value={aiInstruction}
                onChange={(e) => setAiInstruction(e.target.value)}
                placeholder="What's this email about? (optional) e.g. follow up after the demo"
                className="h-8 flex-1 bg-white border-[#DAD5CA] text-[13px]"
                data-testid="email-ai-instruction"
              />
              <Button size="sm" onClick={draftWithAI} disabled={drafting}
                className="h-8 bg-[#1A1A1A] hover:bg-[#333333] text-white text-xs shrink-0" data-testid="email-ai-draft-btn">
                {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />} Draft with AI
              </Button>
            </div>
            <RichTextEditor ref={editorRef} value={compose.body_html} resetKey={resetKey}
              onChange={(html) => setCompose((c) => ({ ...c, body_html: html }))} />
            <p className="text-[11px] text-[#8A857A] mt-1.5">Your EQUIPR signature is added automatically below the message.</p>
          </div>

          {/* Token palette — click to insert into the message (or subject if focused there) */}
          <div className="rounded-[10px] border border-[#E6E2D9] bg-[#FBFAF7] p-3" data-testid="email-token-palette">
            <p className="text-[11px] font-semibold text-[#59554D] uppercase tracking-wide mb-2">Insert a token</p>
            <div className="flex flex-wrap gap-1.5">
              {tokens.map((t) => (
                <button key={t.key} type="button" onClick={() => insertToken(t.key)}
                  title={t.eg ? `e.g. ${t.eg}` : `{{${t.key}}}`}
                  data-testid={`email-token-${t.key}`}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white border border-[#DAD5CA] text-[12px] text-[#1A1A1A] hover:border-[#E0B800] hover:bg-[#FFF8D6] transition-colors">
                  <span className="font-medium">{t.label}</span>
                  <span className="mono text-[10.5px] text-[#8A857A]">{`{{${t.key}}}`}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Attachments — latest quote PDF + library documents + one-off uploads */}
          <div className="rounded-[10px] border border-[#E6E2D9] bg-[#FBFAF7] p-3" data-testid="email-attachments"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); onUploadFiles(e.dataTransfer.files); }}
            style={dragging ? { borderColor: "#E0B800", background: "#FFF8D6" } : undefined}>
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <p className="text-[11px] font-semibold text-[#59554D] uppercase tracking-wide flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Attachments</p>
              <div className="flex items-center gap-1.5">
                <Button type="button" size="sm" variant="outline" onClick={openPicker} className="h-7 bg-white border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED] text-[12px]" data-testid="email-attach-doc-btn"><FileText className="w-3.5 h-3.5 mr-1" /> From Documents</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="h-7 bg-white border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED] text-[12px]" data-testid="email-attach-upload-btn">{uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Upload file</Button>
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => onUploadFiles(e.target.files)} data-testid="email-attach-file-input" />
              </div>
            </div>

            {/* Latest quote — one tick attaches its PDF */}
            {!quoteId && latestQuote && (
              <label className="flex items-center gap-2 bg-white border border-[#E6E2D9] rounded-md px-2.5 py-2 mb-2 cursor-pointer select-none"
                data-testid="email-attach-latest-quote">
                <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)}
                  className="w-4 h-4 accent-[#8A6D00] cursor-pointer" data-testid="email-attach-latest-quote-checkbox" />
                <FileText className="w-4 h-4 text-[#8A6D00] shrink-0" />
                <span className="text-[12.5px] text-[#1A1A1A] truncate flex-1">
                  Latest quote <strong>{latestQuote.quote_number}</strong>
                  <span className="text-[#8A857A]"> · {money(latestQuote.totals?.one_off?.total ?? latestQuote.totals?.total ?? latestQuote.hs_amount)}{latestQuote.status ? ` · ${latestQuote.status}` : ""}</span>
                </span>
                <span className="text-[10px] uppercase tracking-wide text-[#8A857A] bg-[#F1EEE6] px-1.5 py-0.5 rounded">PDF</span>
              </label>
            )}

            {attachments.length === 0 ? (
              <p className="text-[12px] text-[#8A857A]">Drag files here, upload a one-off file, or add approved files from your Documents library.</p>
            ) : (
              <div className="space-y-1.5">
                {attachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white border border-[#E6E2D9] rounded-md px-2.5 py-1.5" data-testid={`email-attach-chip-${i}`}>
                    <FileText className="w-4 h-4 text-[#8A6D00] shrink-0" />
                    <span className="text-[12.5px] text-[#1A1A1A] truncate flex-1" title={a.filename}>{a.filename}</span>
                    {a.kind === "upload" && <span className="text-[10px] uppercase tracking-wide text-[#8A857A] bg-[#F1EEE6] px-1.5 py-0.5 rounded">upload</span>}
                    {a.size ? <span className="mono text-[11px] text-[#8A857A]">{fmtBytes(a.size)}</span> : null}
                    <button type="button" onClick={() => removeAttachment(i)} className="w-6 h-6 rounded flex items-center justify-center text-[#8A857A] hover:text-[#C23A00] hover:bg-[#FFE9E0]" data-testid={`email-attach-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className={`text-[11px] mt-1.5 ${overCap ? "text-[#C23A00] font-semibold" : "text-[#8A857A]"}`} data-testid="email-attach-total">
              {fmtBytes(totalSize)} of 40 MB{overCap ? " — over the limit, remove some files" : ""}{quoteFilename ? ` · ${quoteFilename} also attached` : ""}
            </div>
          </div>
        </div>

        {/* RIGHT — live preview */}
        <div className="lg:sticky lg:top-4">
          <div className="bg-white border border-[#DAD5CA] rounded-xl overflow-hidden" style={{ boxShadow: "var(--shadow-card)" }}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#E6E2D9] bg-[#FBFAF7]">
              <span className="text-[13px] font-bold text-[#1A1A1A] flex items-center gap-1.5" style={{ fontFamily: "Manrope, sans-serif" }}>
                <Eye className="w-4 h-4 text-[#8A6D00]" /> Preview
              </span>
              {previewing && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#8A857A]" />}
            </div>
            {compose.subject && <div className="px-4 pt-3 text-[13px] font-semibold text-[#1A1A1A]" data-testid="email-preview-subject">{previewHtml ? undefined : compose.subject}</div>}
            <div className="p-4 text-[13px] text-black max-h-[70vh] overflow-y-auto" data-testid="email-preview">
              {previewHtml
                ? <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                : <p className="text-[#8A857A] text-center py-10">Start writing to see the preview.</p>}
            </div>
            {(attachments.length > 0 || quoteFilename) && (
              <div className="px-4 py-3 border-t border-[#E6E2D9] bg-[#FBFAF7]" data-testid="email-preview-attachments">
                <p className="text-[11px] font-semibold text-[#59554D] uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
                  <Paperclip className="w-3.5 h-3.5" /> {attachments.length + (quoteFilename ? 1 : 0)} attachment{attachments.length + (quoteFilename ? 1 : 0) === 1 ? "" : "s"}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {quoteFilename && (
                    <span className="inline-flex items-center gap-1.5 bg-white border border-[#E6E2D9] rounded-md px-2 py-1 text-[12px] text-[#1A1A1A]">
                      <FileText className="w-3.5 h-3.5 text-[#8A6D00]" /> {quoteFilename}
                    </span>
                  )}
                  {attachments.map((a, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 bg-white border border-[#E6E2D9] rounded-md px-2 py-1 text-[12px] text-[#1A1A1A]" title={a.filename}>
                      <FileText className="w-3.5 h-3.5 text-[#8A6D00]" />
                      <span className="max-w-[180px] truncate">{a.filename}</span>
                      {a.size ? <span className="mono text-[10.5px] text-[#8A857A]">{fmtBytes(a.size)}</span> : null}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Document picker */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="bg-white max-w-2xl" data-testid="email-doc-picker">
          <DialogHeader><DialogTitle>Attach from Documents</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2">
            <Input value={docSearch} onChange={(e) => setDocSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchDocs()}
              placeholder="Search documents by title, category…" className="bg-[#F4F2ED] border-[#DAD5CA]" data-testid="email-doc-search" />
            <Button type="button" variant="outline" onClick={fetchDocs} className="border-[#DAD5CA] bg-white text-[#59554D] hover:bg-[#F4F2ED]"><Search className="w-4 h-4" /></Button>
          </div>
          <div className="max-h-[360px] overflow-y-auto border border-[#E6E2D9] rounded-lg divide-y divide-[#F0EDE6]">
            {docLoading ? (
              <div className="p-6 text-center text-[#8A857A] text-[13px]"><Loader2 className="w-4 h-4 animate-spin inline mr-1.5" /> Loading…</div>
            ) : docList.length === 0 ? (
              <div className="p-6 text-center text-[#8A857A] text-[13px]">No documents found.</div>
            ) : docList.map((d) => (
              <label key={d.id} className="flex items-center gap-3 px-3 py-2 hover:bg-[#FBFAF7] cursor-pointer" data-testid={`email-doc-row-${d.id}`}>
                <input type="checkbox" checked={!!docSelected[d.id]} onChange={(e) => setDocSelected((s) => ({ ...s, [d.id]: e.target.checked }))} className="w-4 h-4 accent-[#8A6D00]" />
                <FileText className="w-4 h-4 text-[#8A6D00] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-[#1A1A1A] truncate">{d.title}</div>
                  <div className="text-[11px] text-[#8A857A] truncate">{[d.category, d.department, d.current_filename].filter(Boolean).join(" · ")}</div>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setPickerOpen(false)} className="text-[#59554D]">Cancel</Button>
            <Button type="button" onClick={addSelectedDocs} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] font-semibold" data-testid="email-doc-add-btn">Add selected</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
